import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPinIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import DeliveryAddressEditor from "../../dashboard/customers/deliveryAddresses/DeliveryAddressEditor";
import { PMEntityPicker } from "./PMEntityPicker";
import { IconAvatar, PICKER_PAGE_SIZE, rowsFrom } from "./pickerParts";

export function formatDeliveryAddress(row) {
  if (!row) return "";
  if (row.full_address) return row.full_address;
  const city = [row.postal, row.place].filter(Boolean).join(" ");
  const country = row.country_code || row.country;
  return [row.address, row.address2, city, country].filter(Boolean).join(", ");
}

export const addressToItem = (row) =>
  row?.id == null
    ? null
    : {
        id: row.id,
        primary: row.name || row.address || row.displayTitle || `#${row.id}`,
        secondary: [row.address, row.postal, row.place, row.country_code || row.country].filter(Boolean).join(", ") || null,
        leading: <IconAvatar icon={MapPinIcon} />,
        object: row,
      };

export async function fetchAddressOptions({ query, filter, customerId }) {
  const byCustomer = filter === "customer" && customerId != null;
  const allCustomers = filter === "customers";
  const filters = byCustomer
    ? { 0: { name: "CUSTOMER", value: String(customerId) } }
    : allCustomers
      ? { 0: { name: "CUSTOMERS", value: "1" } }
      : undefined;
  const response = await axios.get("/delivery_addresses/", {
    params: {
      query: query || undefined,
      page: 1,
      per_page: PICKER_PAGE_SIZE,
      sortBy: "name",
      sort: "asc",
      filters,
    },
  });
  return rowsFrom(response).map(addressToItem);
}

function matchesQuery(item, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;
  return `${item?.primary || ""} ${item?.secondary || ""}`.toLowerCase().includes(needle);
}

export const COMPANY_ADDRESS_FIELDS = "id,delivery_address,postal_address,physical_address";

/** GET /company is `{ data: company }`; axios itself also wraps the body in `data`. */
export function companyFromCompanyResponse(responseOrPayload) {
  const body = responseOrPayload?.data !== undefined && !Array.isArray(responseOrPayload?.data)
    ? responseOrPayload.data
    : responseOrPayload;
  if (!body || typeof body !== "object") return null;
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) return body.data;
  return body;
}

/** Postal/delivery address first; visiting address only when that is the remaining record. */
export function defaultCompanyPurchaseAddress(company) {
  if (company?.postal_address?.id) return company.postal_address;
  if (company?.delivery_address?.id) return company.delivery_address;
  if (company?.physical_address?.id) return company.physical_address;
  return null;
}

function labeledCompanyAddressItem(row, label) {
  const item = addressToItem(row);
  if (!item) return null;
  if (label) item.primary = [label, item.primary].filter(Boolean).join(" · ");
  return item;
}

/** Company postal and visiting addresses, labeled and de-duplicated by id. */
export async function fetchCompanyAddressOptions({ query, t }) {
  const response = await axios.get("/company", { params: { fields: COMPANY_ADDRESS_FIELDS } });
  const company = companyFromCompanyResponse(response);
  const postalLabel = t?.("pickers.address.companyPostal");
  const physicalLabel = t?.("pickers.address.companyPhysical");
  const bothLabel = t?.("pickers.address.companyPostalAndPhysical");
  const postal = company?.postal_address?.id ? company.postal_address : company?.delivery_address;
  const physical = company?.physical_address;
  const sameRecord = postal?.id && physical?.id && String(postal.id) === String(physical.id);
  const rows = sameRecord
    ? [{ row: postal, label: bothLabel || postalLabel }]
    : [
        { row: postal, label: postalLabel },
        { row: physical, label: physicalLabel },
      ];
  const seen = new Set();
  return rows.flatMap(({ row, label }) => {
    if (!row?.id || seen.has(row.id)) return [];
    seen.add(row.id);
    const item = labeledCompanyAddressItem(row, label);
    return item && matchesQuery(item, query) ? [item] : [];
  });
}

/** Delivery addresses already stored on projects. Does not create a new address model. */
export async function fetchProjectAddressOptions({ query, projectId }) {
  const response = await axios.get("/projects", {
    params: {
      query: query || undefined,
      page: 1,
      per_page: PICKER_PAGE_SIZE,
      fields: "id,displayTitle,delivery_address",
    },
  });
  const projects = rowsFrom(response);
  if (projectId && !query && !projects.some((project) => String(project.id) === String(projectId) && project.delivery_address?.id)) {
    const selected = await axios.get(`/projects/${projectId}`, { params: { fields: "id,displayTitle,delivery_address" } });
    const project = selected?.data?.id ? selected.data : selected?.data?.data;
    if (project?.id) projects.unshift(project);
  }
  const seen = new Set();
  return projects.flatMap((project) => {
    const address = project.delivery_address;
    if (!address?.id || seen.has(address.id)) return [];
    seen.add(address.id);
    const item = addressToItem({ ...address, name: address.name || address.displayTitle || project.displayTitle });
    return item && matchesQuery(item, query) ? [item] : [];
  });
}

/**
 * Delivery address picker (new design). Defaults to the customer's addresses
 * with a switch to the whole company register. "New address" / edit open the
 * delivery address editor; create links the address to the customer.
 * Emits `onChange({ target: { name, value, data } })` and `onSelectedItemChange(row)`.
 */
export function DeliveryAddressPicker({
  name = "delivery_address_id",
  label,
  value,
  customerId,
  projectId,
  addressSet = "register",
  onChange,
  onSelectedItemChange,
  setFieldValue,
  disabled = false,
  error,
  allowCreate = true,
  allowEdit = true,
}) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const hasCustomer = customerId != null && customerId !== "";
  const forPurchase = addressSet === "purchase";
  const selectedId = value?.id ?? null;
  const canEdit = allowEdit && !disabled && selectedId != null;

  const filters = useMemo(
    () => forPurchase
      ? [
          { key: "company", label: t("pickers.address.filterCompany") },
          { key: "project", label: t("pickers.address.filterProject") },
          { key: "customer", label: t("pickers.address.filterCustomer") },
        ]
      : [
          ...(hasCustomer ? [{ key: "customer", label: t("pickers.address.filterCustomer") }] : []),
          { key: "all", label: t("pickers.address.filterAll") },
        ],
    [forPurchase, hasCustomer, t]
  );

  const loadOptions = useCallback(
    ({ query, filter }) => {
      if (!forPurchase) return fetchAddressOptions({ query, filter, customerId });
      if (filter === "project") return fetchProjectAddressOptions({ query, projectId });
      if (filter === "customer") {
        return fetchAddressOptions({ query, filter: hasCustomer ? "customer" : "customers", customerId });
      }
      return fetchCompanyAddressOptions({ query, t });
    },
    [customerId, forPurchase, hasCustomer, projectId, t]
  );

  const emit = (row) => {
    const id = row?.id ?? null;
    setFieldValue?.(name, id);
    onSelectedItemChange?.(row ?? null);
    onChange?.({ target: { name, value: id, data: row ?? null } });
  };

  return (
    <>
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <PMEntityPicker
            label={label ?? t("reportWizard.deliveryAddress")}
            labelAction={canEdit ? { action: () => setEditOpen(true) } : null}
            value={addressToItem(value)}
            placeholder={t("pickers.address.placeholder")}
            searchPlaceholder={t("pickers.address.search")}
            disabled={disabled}
            error={error}
            loadOptions={loadOptions}
            filters={filters}
            defaultFilter={forPurchase ? "company" : hasCustomer ? "customer" : "all"}
            pageSize={PICKER_PAGE_SIZE}
            onSelect={(item) => emit(item?.object ?? null)}
            emptyText={t("pickers.address.empty")}
            footerAction={allowCreate ? { label: t("pickers.address.create"), onClick: () => setCreateOpen(true) } : undefined}
          />
        </div>
        {canEdit ? (
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="mb-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] border-[1.5px] border-slate-200 bg-white text-slate-600 hover:border-[#1463C7] hover:text-[#1463C7]"
            aria-label={t("delivery_address.edit")}
            title={t("delivery_address.edit")}
          >
            <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {createOpen && (
        <DeliveryAddressEditor
          module={hasCustomer ? "CUSTOMER" : undefined}
          moduleParam={hasCustomer ? customerId : undefined}
          onClose={() => setCreateOpen(false)}
          onSaved={(row) => {
            setCreateOpen(false);
            if (row?.id != null) emit(row);
          }}
        />
      )}
      {editOpen && selectedId != null && (
        <DeliveryAddressEditor
          addressId={selectedId}
          onClose={() => setEditOpen(false)}
          onSaved={(row) => {
            setEditOpen(false);
            if (row?.id != null) emit(row);
          }}
        />
      )}
    </>
  );
}
